import React from 'react'
import { MessageCircle, Loader2 } from 'lucide-react'
import { formatDateRelative, formatTime } from '@/utils/dateTimeUtils'
import { useAppSelector } from '@/redux/hooks'
import { cn } from '@/lib/utils'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'

interface NegotiationHistoryMessagesProps {
  messagesContainerRef: React.RefObject<HTMLDivElement>
  messagesEndRef: React.RefObject<HTMLDivElement>
  onScroll: () => void
}

const NegotiationHistoryMessages: React.FC<NegotiationHistoryMessagesProps> = ({
  messagesContainerRef,
  messagesEndRef,
  onScroll,
}) => {
  const { messages, loading, loadingMore, hasMore } = useAppSelector(
    (state) => state.negotiationHistory
  )
  const { user } = useEffectiveUser()
  return (
    <div
      ref={messagesContainerRef}
      className='flex-1 overflow-y-auto p-4 space-y-4 bg-slate-100 h-full'
      onScroll={onScroll}
    >
      {loading && messages.length === 0 && (
        <div className='flex flex-col items-center justify-center h-full text-gray-500'>
          <Loader2 className='w-8 h-8 animate-spin mb-2' />
          <p className='text-sm'>Loading messages...</p>
        </div>
      )}

      {loadingMore && (
        <div className='flex items-center justify-center py-2'>
          <Loader2 className='w-4 h-4 animate-spin mr-2' />
          <span className='text-xs text-gray-500'>
            Loading older messages...
          </span>
        </div>
      )}

      {!hasMore && messages.length > 0 && (
        <div className='flex items-center justify-center py-2'>
          <div className='bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full'>
            Beginning of conversation
          </div>
        </div>
      )}

      {messages.length === 0 && !loading ? (
        <div className='flex flex-col items-center justify-center h-full text-gray-500'>
          <MessageCircle className='w-12 h-12 mb-3 text-gray-300' />
          <p className='text-sm font-medium'>No messages</p>
        </div>
      ) : (
        <>
          {messages.map((message, index) => {
            const isCurrentUser = message.sender_id === user?.id
            const showDate =
              index === 0 ||
              formatDateRelative(message.entrytime) !==
                formatDateRelative(messages[index - 1].entrytime)

            return (
              <div key={`${message.id}-${index}`} className='animate-fade-in'>
                {showDate && (
                  <div className='flex items-center justify-center my-4'>
                    <div className='bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full font-medium'>
                      {formatDateRelative(message.entrytime)}
                    </div>
                  </div>
                )}

                <div
                  className={cn(
                    'flex mb-2',
                    isCurrentUser ? 'justify-end' : 'justify-start'
                  )}
                >
                  <div
                    className={cn(
                      'max-w-[85%] px-4 py-3 rounded-2xl shadow-sm transition-all duration-200 hover:shadow-md',
                      isCurrentUser
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-black rounded-br-md'
                        : 'bg-white text-gray-800 rounded-bl-md border border-gray-100'
                    )}
                  >
                    <div className='text-sm leading-relaxed break-words'>
                      {message.message || JSON.stringify(message)}
                    </div>

                    <div
                      className={cn(
                        'flex items-center justify-between mt-2 gap-2',
                        isCurrentUser ? 'text-blue-100' : 'text-gray-500'
                      )}
                    >
                      <span className='text-xs font-medium'>
                        {isCurrentUser ? 'You' : message.sender_name}
                      </span>
                      <span className='text-xs opacity-75'>
                        {message.entrytime && formatTime(message.entrytime)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
          <div ref={messagesEndRef} />
        </>
      )}
    </div>
  )
}

export default NegotiationHistoryMessages
